import { FC, PropsWithChildren, useCallback, useState } from 'react'
import { DialogTitle, DialogActions, DialogContent, Button, Box, Autocomplete, TextField } from '@mui/material'
import { Contractor } from 'src/api'
import { DataLoad, PropsInjector } from 'src/components'
import { useContractorListRequest } from 'src/hooks/api'
import { useDialog } from 'src/hooks/ui'
import { DialogNames } from 'src/enums'

const ContractorsLoader: FC<PropsWithChildren> = ({ children }) => {
  const { isInitialLoading, isError, data } = useContractorListRequest({
    params: { limit: 9999 }
  })

  if (isInitialLoading || isError)
    return (
      <Box p={8}>
        <DataLoad isLoading={isInitialLoading} isError={isError} />
      </Box>
    )

  const props = { contractors: data?.rows || [] }

  return <PropsInjector props={props}>{children}</PropsInjector>
}

interface AssignSensorToContractorDialogProps {
  contractors?: Contractor[]
  onSubmit?: (contractorId: number | null, contractorName: string) => void
  dialogName?: DialogNames
}

const NO_CONTRACTOR_ID = 'no_contractor'

const AssignSensorToContractorDialog: FC<AssignSensorToContractorDialogProps> = ({ contractors = [], onSubmit = () => {}, dialogName }) => {
  const { closeDialog } = useDialog(dialogName || DialogNames.AssignSensorToContractor)
  const [selectedContractorId, setSelectedIdContractor] = useState<any>(null)
  const update = (event: any, value: { id: number } | null) => {
    if (selectedContractorId !== value?.id) setSelectedIdContractor(value?.id || null)
  }
  const submit = () => {
    if (selectedContractorId !== null) {
      const contractorName = options.find((o) => o.id === selectedContractorId)?.name || ''
      onSubmit(selectedContractorId === NO_CONTRACTOR_ID ? null : (selectedContractorId as number), contractorName)
      closeDialog()
    }
  }
  const options = useCallback(() => {
    const _options: { id: number | string; name: string }[] = contractors.map((contractor) => ({ id: contractor.id, name: contractor.name }))
    _options.unshift({ id: NO_CONTRACTOR_ID, name: 'No contractor' })
    return _options
  }, [contractors])()

  return (
    <>
      <DialogTitle>Select contractor</DialogTitle>
      <DialogContent>
        <Box width="400px" maxWidth="100%" py={2}>
          <Autocomplete
            id="contractors"
            options={options || []}
            getOptionLabel={(option) => option.name || ''}
            style={{ width: '100%' }}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            renderInput={(params) => <TextField {...params} label="Contractor" placeholder="Contractor name..." />}
            value={options.find((option) => option.id === selectedContractorId) || null}
            onChange={(event, value) => update(event, value as { id: number })}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={() => closeDialog()}>
          Cancel
        </Button>
        <Button variant="contained" disabled={selectedContractorId === null} onClick={submit}>
          Assign
        </Button>
      </DialogActions>
    </>
  )
}

export const AssignSensorToContractor: FC<{
  onSubmit: (contractorId: number | null, contractorName: string) => void
  dialogName?: DialogNames
}> = ({ onSubmit, dialogName }) => {
  return (
    <ContractorsLoader>
      <AssignSensorToContractorDialog onSubmit={onSubmit} dialogName={dialogName} />
    </ContractorsLoader>
  )
}
